import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';

const STEP_KEYS = ['profile', 'invoice', 'funded'] as const;

export type FinancingStepsProps = {
  /**
   * Optional override — by default reads
   * `landing.steps.{title,subhead}` from the `m-financing`
   * namespace. Pass these props on a marketing landing page that
   * uses its own copy.
   */
  title?: string;
  subhead?: string;
  className?: string;
};

/**
 * Three numbered step cards explaining the financing flow:
 * profile → invoice → funded. Card layout collapses to a single
 * column on small screens, three columns on `md+`.
 */
export function FinancingSteps({ title, subhead, className }: FinancingStepsProps) {
  const { t } = useTranslation('m-financing');

  return (
    <section className={className}>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
          {title ?? t('landing.steps.title')}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {subhead ?? t('landing.steps.subhead')}
        </p>
      </div>

      <div className={cn('grid grid-cols-1 gap-4 md:grid-cols-3')}>
        {STEP_KEYS.map(key => (
          <article
            key={key}
            className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 transition-shadow hover:shadow-md">
            <div className="absolute top-4 end-5 text-5xl font-extrabold text-emerald-100">
              {t(`landing.steps.items.${key}.number`)}
            </div>
            <h3 className="relative text-lg font-semibold text-gray-900">
              {t(`landing.steps.items.${key}.title`)}
            </h3>
            <p className="relative mt-2 text-sm leading-relaxed text-gray-600">
              {t(`landing.steps.items.${key}.description`)}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}
